import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { auth, db, storage } from "../Config/firebase";
import Navbar from "../Components/Navbar/navbar";
import { compressImage } from "../../utils/compressImage";
import RelatorioTracaoPrint from "./RelatorioTracaoPrint";
import "../CadastroExtracoes/DetalhesExtracao.css";
import { consultaTenantAtual } from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const METODOLOGIAS_POR_TIPO = {
  A:
    "O ensaio segue as prescrições das normas da ABNT:\n" +
    "√ ABNT NBR 13749 - Revestimento de paredes e tetos de argamassas inorgânicas - Especificação.\n" +
    "√ ABNT NBR 13528 - Revestimento de paredes de argamassas inorgânicas - Determinação da resistência de aderência à tração.",
  B:
    "O ensaio segue as prescrições das normas da ABNT:\n" +
    "√ ABNT NBR 13754 - Revestimento de paredes internas com placas cerâmicas e com utilização de argamassa colante - procedimento - Anexo A - Determinação da resistência de aderência de revestimentos cerâmicos assentados com argamassa colante.",
  C:
    "O ensaio segue as prescrições das normas da ABNT:\n" +
    "√ ABNT NBR 13755 - Revestimentos cerâmicos de fachadas e paredes externas com utilização de argamassa colante — Projeto, execução, inspeção e aceitação — Procedimento,",
  D:
    "O ensaio segue as prescrições das normas da ABNT:\n" +
    "√ ABNT NBR 13753 - Revestimento de paredes internas com placas cerâmicas e com utilização de argamassa colante - procedimento - Anexo A - Determinação da resistência de aderência de revestimentos cerâmicos assentados com argamassa colante.",
};

const TITULOS_POR_TIPO = {
  A: "Relatório de ensaio - Determinação da resistência de aderência à tração de revestimentos de argamassa",
  B: "Relatório de ensaio - Determinação da resistência de aderência à tração de paredes internas com placas cerâmicas e com utilização de argamassa colante",
  C: "Relatório de ensaio - Determinação da resistência de aderência à tração de paredes externas com utilização de argamassa colante",
  D: "Relatório de ensaio - Revestimento de piso interno ou externo com placas cerâmicas e com utilização de argamassa colante - Procedimento",
};

const normalizarTipoEnsaio = (tipo) => {
  if (["A", "B", "C", "D"].includes(tipo)) return tipo;
  return tipo === "placaQuadrada" ? "B" : "A";
};

const usaPlacaQuadrada = (tipo) => normalizarTipoEnsaio(tipo) !== "A";

const ACEITACAO_PADRAO =
  "O revestimento deve ser aceito se de cada grupo de 12 ensaios realizados, com idade igual ou superior a 28 dias, pelo menos oito valores forem iguais ou superiores aos indicados na tabela abaixo:";

const ACEITACAO_TIPO_D =
  "Para os 12 corpos de prova, considerados como dois grupos de seis determinações, o ensaio será aceito quando, em cada grupo, pelo menos quatro valores forem iguais ou superiores a 0,30 MPa, após a cura de 28 dias da argamassa colante utilizada no assentamento.";

const TEXTO_RESUMO_QUANTIDADE_TIPO_D_PADRAO = "Valores acima de 0,30 MPa";
const TEXTO_CRITERIO_RESUMO_TIPO_D_PADRAO =
  "Critério: mínimo de 8 resultados ≥ 0,30 MPa em cada grupo de 12";

const TABELA_ACEITACAO_PADRAO = [
  { local: "Parede interna", acabamento: "Pintura ou base para reboco", ra: "≥ 0,20" },
  { local: "Parede interna", acabamento: "Cerâmica ou laminado", ra: "≥ 0,30" },
  { local: "Parede externa", acabamento: "Pintura, reboco ou cerâmica", ra: "≥ 0,30" },
  { local: "Teto", acabamento: "-", ra: "≥ 0,20" },
];

const TABELA_ACEITACAO_TIPO_D = [
  { local: "Piso interno ou externo", acabamento: "Placas cerâmicas", ra: "≥ 0,30" },
];

const tabelaAceitacaoIgual = (tabelaA, tabelaB) =>
  JSON.stringify(tabelaA) === JSON.stringify(tabelaB);

const EQUIPAMENTO_CORTE_PADRAO =
  "Marca Furadeira bosch com serra copo diamantada";
const COLA_UTILIZADA_PADRAO = "Sikadur Epoxi Tix";

const rupturas = [
  "substrato",
  "substratoChapisco",
  "chapisco",
  "chapiscoArgamassa",
  "argamassa",
  "argamassaCola",
  "colaPastilha",
];

const rotulos = {
  substrato: "Substrato",
  substratoChapisco: "Substrato/Chapisco",
  chapisco: "Chapisco",
  chapiscoArgamassa: "Chapisco/Argamassa",
  argamassa: "Argamassa",
  argamassaCola: "Argamassa/Cola",
  colaPastilha: "Cola/Pastilha",
};

const numero = v => Number(String(v || "0").replace(",", "."));

const dataBR = v =>
  v && v.includes("-") ? v.split("-").reverse().join("/") : v || "-";

function valorRuptura(cp, tipo) {
  if (cp.rupturasPercentuais?.[tipo] !== undefined) {
    return cp.rupturasPercentuais[tipo];
  }

  if (cp.formaRuptura === tipo) {
    return cp.percentualRuptura || "";
  }

  return "";
}

function somaRupturas(cp) {
  return rupturas.reduce((total, tipo) => {
    const valor = Number(
      String(cp.rupturasPercentuais?.[tipo] || 0).replace(",", ".")
    );

    return total + (isNaN(valor) ? 0 : valor);
  }, 0);
}

function atualizarRuptura(cp, tipo, valor) {
  return {
    ...cp,
    rupturasPercentuais: {
      ...(cp.rupturasPercentuais || {}),
      [tipo]: valor,
    },
  };
}

function calcular(cp, tipoEnsaio = "A") {
  const d1 = numero(cp.d1);
  const d2 = numero(cp.d2);
  const placaQuadrada = usaPlacaQuadrada(tipoEnsaio);

  const media =
    d1 && d2
      ? (d1 + d2) / 2
      : 0;

  const area =
    placaQuadrada && d1 > 0 && d2 > 0
      ? d1 * d2
      : media > 0
      ? (Math.PI * media * media) / 4
      : 0;

  const cargaTexto = String(cp.cargaRuptura ?? "").trim();

  const carga =
    cargaTexto === ""
      ? null
      : numero(cargaTexto);

  let tensao = "";

  if (carga !== null && area > 0) {
    tensao = ((carga * 9.80665) / area).toFixed(3);
  }

  return {
    ...cp,
    diametroMedio: !placaQuadrada && media ? media.toFixed(1) : "",
    area: area ? area.toFixed(1) : "",
    tensao,
  };
}

function fotoVazia(i) {
  return {
    id: `foto-${i + 1}`,
    legenda: `FOTO ${String(i + 1).padStart(2, "0")}`,
    url: "",
    path: "",
  };
}

export default function DetalhesTracao() {
  const { tracaoId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [ensaio, setEnsaio] = useState(null);
  const [corpos, setCorpos] = useState([]);
  const [preencherDimensoesTodos, setPreencherDimensoesTodos] = useState(false);
  const [print, setPrint] = useState(false);
  const [fotosAberto, setFotosAberto] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [progressoFotos, setProgressoFotos] = useState(0);
  const [mensagem, setMensagem] = useState("");
  const [certificadosEquipamentos, setCertificadosEquipamentos] = useState([]);
  const [temAlteracoes, setTemAlteracoes] = useState(false);
  const [openConfirmarCancelamento, setOpenConfirmarCancelamento] = useState(false);
  const [openAvisoCelulaCarga, setOpenAvisoCelulaCarga] = useState(false);
  const [navegacaoPendente, setNavegacaoPendente] = useState(null);
  const desbloquearNavegacaoRef = useRef(null);
  const ensaioOriginalRef = useRef(null);

  useEffect(() => {
    carregar();
    carregarCertificadosEquipamentos();
  }, [tracaoId]);
  useEffect(() => {
    if (location.search.includes("relatorio=true")) {
      setPrint(true);
    }
  }, [location.search]);

  useEffect(() => {
    function confirmarSaida(event) {
      if (!temAlteracoes) return;

      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", confirmarSaida);
    return () => window.removeEventListener("beforeunload", confirmarSaida);
  }, [temAlteracoes]);

  useEffect(() => {
    if (!temAlteracoes) return undefined;

    desbloquearNavegacaoRef.current = history.block((locationBloqueada) => {
      setNavegacaoPendente(locationBloqueada);
      setOpenConfirmarCancelamento(true);
      return false;
    });

    return () => {
      desbloquearNavegacaoRef.current?.();
      desbloquearNavegacaoRef.current = null;
    };
  }, [history, temAlteracoes]);

  async function carregarCertificadosEquipamentos() {
  const [equipamentosSnap, documentosSnap] = await Promise.all([
    getDocs(consultaTenantAtual(collection(db, "equipamentos"))),
    getDocs(consultaTenantAtual(collection(db, "eq_documentos"))),
  ]);

  const documentos = documentosSnap.docs.map(documento => ({
    id: documento.id,
    ...documento.data(),
  }));

  const equipamentosComCertificado = await Promise.all(
    equipamentosSnap.docs.map(async documento => {
      const equipamento = {
        id: documento.id,
        ...documento.data(),
      };

      const certificado = documentos.find(
  item =>
    item.equipamentoId === equipamento.id &&
    item.statusDocumento === "ENVIADO" &&
    (
      item.tipoDocumentoCodigo === "CERTIFICADO" ||
      String(item.tipoDocumentoNome || "")
        .toUpperCase()
        .includes("CERTIFICADO")
    )
);

      let url = "";

      if (certificado?.storagePathAtual) {
        try {
          url = await getDownloadURL(ref(storage, certificado.storagePathAtual));
        } catch {
          url = "";
        }
      }

      return {
        id: equipamento.id,
        equipamentoId: equipamento.id,
        equipamentoNome: equipamento.nome || "",
        equipamentoDescricao: equipamento.descricao || "",
        certificadoId: certificado?.id || "",
        numeroCertificado: certificado?.numeroCertificado || "",
        nomeArquivoOriginal: certificado?.nomeArquivoOriginal || "",
        storagePathAtual: certificado?.storagePathAtual || "",
        url,
      };
    })
  );

  setCertificadosEquipamentos(
  equipamentosComCertificado
    .filter(item => item.certificadoId)
    .sort((a, b) =>
      String(a.equipamentoNome || "").localeCompare(
        String(b.equipamentoNome || ""),
        "pt-BR"
      )
    )
);

}

  async function carregar() {
    const s = await getDoc(doc(db, "tblEnsaiosTracao", tracaoId));
    if (!s.exists()) return;

    const dados = s.data();
    const exibirResumoLimitesPadrao = dados.exibirResumoLimites !== false;
    const tipoEnsaio = normalizarTipoEnsaio(dados.tipoEnsaio);
    const usarAceitacaoTipoD =
      tipoEnsaio === "D" &&
      (dados.textoAceitacao == null || dados.textoAceitacao === ACEITACAO_PADRAO);
    const usarTabelaTipoD =
      tipoEnsaio === "D" &&
      (!Array.isArray(dados.tabelaAceitacao) ||
        tabelaAceitacaoIgual(dados.tabelaAceitacao, TABELA_ACEITACAO_PADRAO));
    const d = {
      id: s.id,
      ...dados,
      tipoEnsaio,
      textoMetodologia:
        dados.textoMetodologia ?? METODOLOGIAS_POR_TIPO[tipoEnsaio],
      textoAceitacao: usarAceitacaoTipoD
        ? ACEITACAO_TIPO_D
        : dados.textoAceitacao ?? ACEITACAO_PADRAO,
      tabelaAceitacao: usarTabelaTipoD
        ? TABELA_ACEITACAO_TIPO_D.map(item => ({ ...item }))
        : Array.isArray(dados.tabelaAceitacao)
        ? dados.tabelaAceitacao
        : TABELA_ACEITACAO_PADRAO.map(item => ({ ...item })),
      equipamentoCorte: dados.equipamentoCorte ?? EQUIPAMENTO_CORTE_PADRAO,
      colaUtilizada: dados.colaUtilizada ?? COLA_UTILIZADA_PADRAO,
      observacoes: dados.observacoes ?? "",
      limiteResumo1: dados.limiteResumo1 ?? "0,20",
      limiteResumo2: dados.limiteResumo2 ?? "0,30",
      exibirResumoLimite1:
        dados.exibirResumoLimite1 ?? exibirResumoLimitesPadrao,
      exibirResumoLimite2:
        dados.exibirResumoLimite2 ?? exibirResumoLimitesPadrao,
      exibirResumoQuantidadeTipoD:
        dados.exibirResumoQuantidadeTipoD ?? (dados.exibirResumoTipoD !== false),
      textoResumoQuantidadeTipoD:
        dados.textoResumoQuantidadeTipoD ?? TEXTO_RESUMO_QUANTIDADE_TIPO_D_PADRAO,
      exibirCriterioResumoTipoD:
        dados.exibirCriterioResumoTipoD ?? (dados.exibirResumoTipoD !== false),
      textoCriterioResumoTipoD:
        dados.textoCriterioResumoTipoD ?? TEXTO_CRITERIO_RESUMO_TIPO_D_PADRAO,
    };
    const corposCalculados = (d.corposProva || []).map(item => calcular(item, d.tipoEnsaio));
    setEnsaio(d);
    setCorpos(corposCalculados);
    ensaioOriginalRef.current = { ...d, corposProva: corposCalculados };
    setPreencherDimensoesTodos(false);
    setTemAlteracoes(false);
    setOpenConfirmarCancelamento(false);
  }

  function campo(nome, valor) {
    setMensagem("");
    setTemAlteracoes(true);
    setEnsaio(e => ({
      ...e,
      [nome]: valor,
    }));
  }

  function campoTabelaAceitacao(index, nome, valor) {
    setMensagem("");
    setTemAlteracoes(true);
    setEnsaio(e => ({
      ...e,
      tabelaAceitacao: (e.tabelaAceitacao || TABELA_ACEITACAO_PADRAO).map(
        (item, itemIndex) =>
          itemIndex === index ? { ...item, [nome]: valor } : item
      ),
    }));
  }

  function adicionarLinhaTabelaAceitacao() {
    setMensagem("");
    setTemAlteracoes(true);
    setEnsaio(e => ({
      ...e,
      tabelaAceitacao: [
        ...(e.tabelaAceitacao || TABELA_ACEITACAO_PADRAO),
        { local: "", acabamento: "", ra: "" },
      ],
    }));
  }

  function excluirLinhaTabelaAceitacao(index) {
    setMensagem("");
    setTemAlteracoes(true);
    setEnsaio(e => ({
      ...e,
      tabelaAceitacao: (e.tabelaAceitacao || TABELA_ACEITACAO_PADRAO).filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  }

  function cp(i, nome, valor) {
    setMensagem("");
    setTemAlteracoes(true);
    setCorpos(c => {
      const repetirDimensao =
        preencherDimensoesTodos &&
        i === 0 &&
        ["d1", "d2"].includes(nome);

      return c.map((x, j) =>
        j === i || repetirDimensao
          ? calcular({ ...x, [nome]: valor }, ensaio.tipoEnsaio)
          : x
      );
    });
  }

  function alternarPreenchimentoDimensoes(ativo) {
    setPreencherDimensoesTodos(ativo);
    if (!ativo) return;

    setMensagem("");
    setTemAlteracoes(true);
    setCorpos(atuais => {
      const primeiro = atuais[0];
      if (!primeiro) return atuais;

      return atuais.map(item =>
        calcular(
          { ...item, d1: primeiro.d1, d2: primeiro.d2 },
          ensaio.tipoEnsaio
        )
      );
    });
  }

  function cpCompleto(i, novoCp) {
    setMensagem("");
    setTemAlteracoes(true);
    setCorpos(c =>
      c.map((x, j) =>
        j === i ? calcular(novoCp, ensaio.tipoEnsaio) : x
      )
    );
  }

  function moverParaLinhaAbaixo(evento, campo, index) {
    if (evento.key !== "Enter") return;

    evento.preventDefault();

    const proximo = document.querySelector(
      `[data-tracao-campo='${campo}'][data-tracao-index='${index + 1}']`
    );

    if (proximo) {
      proximo.focus();
      proximo.select?.();
    }
  }

  function atualizarNumeracao(valorInicial) {
    setMensagem("");
    setTemAlteracoes(true);
    setCorpos(atuais => {
      const valor = String(valorInicial || "");

      if (!valor.trim()) {
        return atuais.map((item) => ({
          ...item,
          numero: "",
        }));
      }

      const partes = valor.match(/^(.*?)(\d+)$/);

      if (!partes) {
        return atuais.map((item, index) => ({
          ...item,
          numero: index === 0 ? valor : item.numero,
        }));
      }

      const prefixo = partes[1];
      const inicio = Number(partes[2]);
      const quantidadeDigitos = partes[2].length;

      return atuais.map((item, index) => ({
        ...item,
        numero: `${prefixo}${String(inicio + index).padStart(
          quantidadeDigitos,
          "0"
        )}`,
      }));
    });
  }

  async function salvar() {
    const existeCargaRupturaDigitada = corpos.some(
      item =>
        item.cargaRuptura !== null &&
        item.cargaRuptura !== undefined &&
        String(item.cargaRuptura).trim() !== ""
    );

    if (
      existeCargaRupturaDigitada &&
      !ensaio.certificadoCelulaCarga?.certificadoId
    ) {
      setOpenAvisoCelulaCarga(true);
      return;
    }

    setSalvando(true);

    try {
      const salvo = await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblEnsaiosTracao", tracaoId),
        original: ensaioOriginalRef.current,
        atual: { ...ensaio, corposProva: corpos },
        preparar: (estado) => ({
          ...estado,
          quantidadeCorposProva: estado.corposProva?.length || 0,
        }),
        metadados: {
          atualizadoEm: new Date(),
          atualizadoPor: auth.currentUser?.email || null,
        },
      });
      const salvoTela = { ...salvo, id: tracaoId };
      setEnsaio(salvoTela);
      setCorpos(salvoTela.corposProva || []);
      ensaioOriginalRef.current = salvoTela;

      setMensagem("Ensaio salvo com sucesso.");
      setTemAlteracoes(false);
    } finally {
      setSalvando(false);
    }
  }

  function solicitarCancelarAlteracoes() {
    setNavegacaoPendente(null);
    setOpenConfirmarCancelamento(true);
  }

  async function confirmarCancelarAlteracoes() {
    setOpenConfirmarCancelamento(false);
    await carregar();

    if (navegacaoPendente) {
      const destino = `${navegacaoPendente.pathname}${navegacaoPendente.search || ""}${navegacaoPendente.hash || ""}`;
      desbloquearNavegacaoRef.current?.();
      desbloquearNavegacaoRef.current = null;
      setNavegacaoPendente(null);
      history.push(destino);
    }
  }

  function abrirFotos() {
    const atuais = ensaio.relatorioFotografico || [];
    const total = Math.max(13, atuais.length);

    setFotos(
      Array.from({ length: total }, (_, i) => ({
        ...fotoVazia(i),
        ...(atuais[i] || {}),
      }))
    );

    setFotosAberto(true);
  }

  function voltarDoRelatorio() {
    const params = new URLSearchParams(location.search);

    if (params.get("origem") === "lista") {
      history.push("/app/resistencia-tracao");
      return;
    }

    setPrint(false);
  }

  async function salvarFotos() {
    setSalvando(true);
    setProgressoFotos(0);

    try {
      const finais = [];
      const totalEtapas = fotos.filter((foto) => foto.arquivo).length + 1;
      let etapaAtual = 0;

      const avancarProgresso = () => {
        etapaAtual += 1;
        setProgressoFotos(Math.round((etapaAtual / totalEtapas) * 100));
      };

      for (let i = 0; i < fotos.length; i++) {
        let f = { ...fotos[i] };

        if (f.arquivo) {
          const blob = await compressImage(f.arquivo, {
            maxWidth: 1400,
            maxHeight: 1400,
            quality: 0.72,
          });

          const path = `tracao/${tracaoId}/relatorio-fotografico/foto-${
            i + 1
          }-${Date.now()}.jpg`;

          await uploadBytes(ref(storage, path), blob, {
            contentType: "image/jpeg",
          });

          if (f.path) {
            deleteObject(ref(storage, f.path)).catch(() => {});
          }

          f = {
            id: f.id,
            legenda: f.legenda,
            path,
            url: await getDownloadURL(ref(storage, path)),
          };

          avancarProgresso();
        }

        finais.push({
          id: f.id,
          legenda: f.legenda,
          path: f.path || "",
          url: f.url || "",
        });
      }

      await updateDoc(doc(db, "tblEnsaiosTracao", tracaoId), {
        relatorioFotografico: finais,
      });
      avancarProgresso();

      setEnsaio(e => ({
        ...e,
        relatorioFotografico: finais,
      }));
      ensaioOriginalRef.current = {
        ...ensaioOriginalRef.current,
        relatorioFotografico: finais,
      };

      setFotosAberto(false);
    } finally {
      setSalvando(false);
      setProgressoFotos(0);
    }
  }

  if (!ensaio) return null;

  const placaQuadrada = usaPlacaQuadrada(ensaio.tipoEnsaio);

  if (print) {
    return (
      <RelatorioTracaoPrint
  ensaio={{ ...ensaio, corposProva: corpos }}
  certificado={ensaio.certificadoCelulaCarga}
  onVoltar={voltarDoRelatorio}
/>
    );
  }

  const limiteResumo1 = numero(ensaio.limiteResumo1);
  const limiteResumo2 = numero(ensaio.limiteResumo2);
  const primeiraDimensaoPreenchida =
    String(corpos[0]?.d1 ?? "").trim() !== "" &&
    String(corpos[0]?.d2 ?? "").trim() !== "";
  const resultadosAcimaLimite1 = corpos.filter(
    item => Number(item.tensao) >= limiteResumo1
  ).length;
  const resultadosAcimaLimite2 = corpos.filter(
    item => Number(item.tensao) >= limiteResumo2
  ).length;
  const resultadosAcima030TipoD = corpos.filter(
    item => numero(item.tensao) >= 0.3
  ).length;
  const tipoD = normalizarTipoEnsaio(ensaio.tipoEnsaio) === "D";
  return (
    <>
      <Navbar />

      <Box className="extracao-actions">
        <Box className="extracao-actions-titulos">
          <Typography component="h1">
            {TITULOS_POR_TIPO[normalizarTipoEnsaio(ensaio.tipoEnsaio)]}
          </Typography>
          <Typography>
            {ensaio.codigo} · Rev.00 ·{" "}
            {dataBR(ensaio.dataRelatorio || ensaio.dataEnsaio)}
          </Typography>
        </Box>

        <Box className="extracao-actions-botoes">
          {temAlteracoes ? (
            <>
              <Tooltip title="Visualizar relatório">
                <span>
                  <IconButton color="primary" disabled aria-label="Visualizar relatório">
                    <VisibilityIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Relatório fotográfico">
                <span>
                  <IconButton color="primary" disabled aria-label="Relatório fotográfico">
                    <PhotoLibraryIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Imprimir relatório">
                <span>
                  <IconButton color="primary" disabled aria-label="Imprimir relatório">
                    <PrintIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button variant="outlined" disabled startIcon={<ArrowBackIcon />}>
                Voltar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={salvando}
                onClick={salvar}
              >
                {salvando ? "Salvando..." : "Salvar"}
              </Button>
              <Button data-confirmacao-cancelamento="propria" variant="outlined" onClick={solicitarCancelarAlteracoes} disabled={salvando}>
                Cancelar alterações
              </Button>
            </>
          ) : (
            <>
              <Tooltip title="Visualizar relatório">
                <IconButton color="primary" onClick={() => setPrint(true)} aria-label="Visualizar relatório">
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Relatório fotográfico">
                <IconButton color="primary" onClick={abrirFotos} aria-label="Relatório fotográfico">
                  <PhotoLibraryIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Imprimir relatório">
                <IconButton color="primary" onClick={() => setPrint(true)} aria-label="Imprimir relatório">
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => history.push("/app/resistencia-tracao")}
              >
                Voltar
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Box className="extracao-page">
        <Typography color="primary" mb={2}>
          {mensagem}
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" mb={2}>
            Dados do sistema de revestimento
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            flexWrap="wrap"
          >
            <TextField
              select
              label="Substrato"
              value={ensaio.substrato || "estruturaConcreto"}
              onChange={e => campo("substrato", e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="blocoCeramico">
                Bloco de vedação - cerâmico
              </MenuItem>
              <MenuItem value="estruturaConcreto">
                Estrutura de concreto
              </MenuItem>
              <MenuItem value="blocoConcreto">
                Bloco de vedação - concreto
              </MenuItem>
              <MenuItem value="outros">Outros</MenuItem>
            </TextField>

            <TextField
              select
              label="Chapisco"
              value={ensaio.chapisco || "nao"}
              onChange={e => campo("chapisco", e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="sim">Sim</MenuItem>
              <MenuItem value="nao">Não</MenuItem>
            </TextField>

            <TextField
              select
              label="Argamassa"
              value={ensaio.argamassa || "naoInformado"}
              onChange={e => campo("argamassa", e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="cimento">Cimento</MenuItem>
              <MenuItem value="mista">Mista</MenuItem>
              <MenuItem value="industrializada">Industrializada</MenuItem>
              <MenuItem value="naoInformado">Não informado</MenuItem>
            </TextField>

            <TextField
              select
              label="Tipo de aplicação"
              value={ensaio.tipoAplicacao || "naoInformado"}
              onChange={e => campo("tipoAplicacao", e.target.value)}
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="mecanica">Mecânica</MenuItem>
              <MenuItem value="naoInformado">Não informado</MenuItem>
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={2}>
            <TextField
              fullWidth
              label="Localização dos ensaios"
              value={ensaio.localEnsaio || ""}
              onChange={e => campo("localEnsaio", e.target.value)}
            />

            <TextField
              label="Idade do revestimento"
              value={ensaio.idadeRevestimento || ""}
              onChange={e => campo("idadeRevestimento", e.target.value)}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={2}>
  <Autocomplete
    fullWidth
    options={certificadosEquipamentos}
    value={
      certificadosEquipamentos.find(
        item => item.certificadoId === ensaio.certificadoCelulaCarga?.certificadoId
      ) || null
    }
    getOptionLabel={option =>
  option
    ? [
        option.equipamentoNome || "Equipamento",
        option.numeroCertificado
          ? `Certificado ${option.numeroCertificado}`
          : option.nomeArquivoOriginal || "Certificado cadastrado",
        option.url ? "" : "arquivo não carregado",
      ]
        .filter(Boolean)
        .join(" - ")
    : ""
}
    isOptionEqualToValue={(option, value) =>
      option.certificadoId === value.certificadoId
    }
    onChange={(event, value) => {
      campo("certificadoCelulaCarga", value || null);
    }}
    renderInput={params => (
      <TextField
        {...params}
        label="Certificado da célula de carga"
        placeholder="Selecione a célula de carga utilizada"
        fullWidth
      />
    )}
  />
</Stack>

        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" mb={2}>
            Textos do relatório
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="3. Metodologia"
              value={ensaio.textoMetodologia || ""}
              onChange={e => campo("textoMetodologia", e.target.value)}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="4. Aceitação"
              value={ensaio.textoAceitacao || ""}
              onChange={e => campo("textoAceitacao", e.target.value)}
              helperText="Deixe vazio para não mostrar o item Aceitação nem sua tabela no relatório."
            />

            {Boolean(String(ensaio.textoAceitacao || "").trim()) && (
              <Box sx={{ overflowX: "auto" }}>
                <Typography fontWeight={700} mb={1}>
                  Tabela 1 - Limites de resistência de aderência à tração (Ra)
                </Typography>

                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Local</TableCell>
                      <TableCell>Acabamento</TableCell>
                      <TableCell>Ra (MPa)</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(ensaio.tabelaAceitacao || TABELA_ACEITACAO_PADRAO).map(
                      (item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.local || ""}
                              onChange={e =>
                                campoTabelaAceitacao(index, "local", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.acabamento || ""}
                              onChange={e =>
                                campoTabelaAceitacao(index, "acabamento", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ width: 150 }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.ra || ""}
                              onChange={e =>
                                campoTabelaAceitacao(index, "ra", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ width: 100 }}>
                            <Button
                              color="error"
                              size="small"
                              onClick={() => excluirLinhaTabelaAceitacao(index)}
                            >
                              Excluir
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>

                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={adicionarLinhaTabelaAceitacao}
                >
                  + Adicionar linha
                </Button>
              </Box>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Equipamento de corte"
                value={ensaio.equipamentoCorte || ""}
                onChange={e => campo("equipamentoCorte", e.target.value)}
              />

              <TextField
                fullWidth
                label="Cola utilizada"
                value={ensaio.colaUtilizada || ""}
                onChange={e => campo("colaUtilizada", e.target.value)}
              />
            </Stack>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="6. Observações adicionais"
              value={ensaio.observacoes || ""}
              onChange={e => campo("observacoes", e.target.value)}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, mb: 2, overflow: "auto" }}>
          <Typography variant="h6" mb={2}>
            Quadro 1 - Resultados da resistência de aderência
          </Typography>

          <Stack direction="row" alignItems="center" mb={2}>
            <Checkbox
              checked={preencherDimensoesTodos}
              disabled={!primeiraDimensaoPreenchida && !preencherDimensoesTodos}
              onChange={e => alternarPreenchimentoDimensoes(e.target.checked)}
            />
            <Typography>
              Preencher todos com {placaQuadrada ? "L1 e L2" : "d1 e d2"} do primeiro corpo de prova
            </Typography>
          </Stack>

          <Table
            size="small"
            sx={{
              minWidth: 1500,
              borderCollapse: "collapse",
              "& th, & td": {
                textAlign: "center",
                border: "1px solid #d0d0d0",
                padding: "6px",
              },
              "& th": {
                backgroundColor: "#eef4e6",
                fontWeight: 600,
              },
              "& .MuiInputBase-input": {
                textAlign: "center",
                padding: "8px 4px",
              },
              "& .MuiSelect-select": {
                textAlign: "center",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2}>nº</TableCell>
                <TableCell colSpan={placaQuadrada ? 2 : 3}>
                  {placaQuadrada ? "Lado (mm)" : "Diâmetro (mm)"}
                </TableCell>
                <TableCell rowSpan={2}>Área (mm²)</TableCell>
                <TableCell colSpan={2}>Local do ensaio</TableCell>
                <TableCell colSpan={7}>Forma de Ruptura (%)</TableCell>
                <TableCell rowSpan={2}>Carga de Ruptura (kgf)</TableCell>
                <TableCell rowSpan={2}>Tensão Ra (MPa)</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>{placaQuadrada ? "L1" : "d1"}</TableCell>
                <TableCell>{placaQuadrada ? "L2" : "d2"}</TableCell>
                {!placaQuadrada && <TableCell>médio</TableCell>}
                <TableCell>Bloco</TableCell>
                <TableCell>Junta</TableCell>
                <TableCell>Substrato</TableCell>
                <TableCell>Substrato/ Chapisco</TableCell>
                <TableCell>Chapisco</TableCell>
                <TableCell>Chapisco/ Argamassa</TableCell>
                <TableCell>Argamassa</TableCell>
                <TableCell>Argamassa/ Cola</TableCell>
                <TableCell>Cola/ Pastilha</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
  {corpos.map((x, i) => {
    const somaPercentuais = somaRupturas(x);
    const erroPercentual = somaPercentuais > 100;

    return (
      <TableRow key={i}>
        <TableCell>
          {i === 0 ? (
            <TextField
              size="small"
              value={x.numero || ""}
              onChange={e => atualizarNumeracao(e.target.value)}
              inputProps={{
                "aria-label": "Número inicial do corpo de prova",
              }}
              sx={{ width: 72 }}
            />
          ) : (
            x.numero
          )}
        </TableCell>

        <TableCell>
          <TextField
            size="small"
            value={x.d1 || ""}
            onChange={e => cp(i, "d1", e.target.value)}
            onKeyDown={e => moverParaLinhaAbaixo(e, "d1", i)}
            inputProps={{
              "data-tracao-campo": "d1",
              "data-tracao-index": i,
            }}
            sx={{ width: 70 }}
          />
        </TableCell>

        <TableCell>
          <TextField
            size="small"
            value={x.d2 || ""}
            onChange={e => cp(i, "d2", e.target.value)}
            onKeyDown={e => moverParaLinhaAbaixo(e, "d2", i)}
            inputProps={{
              "data-tracao-campo": "d2",
              "data-tracao-index": i,
            }}
            sx={{ width: 70 }}
          />
        </TableCell>

        {!placaQuadrada && (
          <TableCell>{String(x.diametroMedio || "").replace(".", ",")}</TableCell>
        )}
        <TableCell>{String(x.area || "").replace(".", ",")}</TableCell>

        <TableCell>
          <Checkbox
            size="small"
            checked={x.local === "bloco"}
            onChange={() => cp(i, "local", x.local === "bloco" ? "" : "bloco")}
          />
        </TableCell>

        <TableCell>
          <Checkbox
            size="small"
            checked={x.local === "junta"}
            onChange={() => cp(i, "local", x.local === "junta" ? "" : "junta")}
          />
        </TableCell>

        {rupturas.map(r => (
          <TableCell key={r}>
            <TextField
              size="small"
              error={erroPercentual}
              value={valorRuptura(x, r)}
              onChange={e =>
                cpCompleto(i, atualizarRuptura(x, r, e.target.value))
              }
              onKeyDown={e => moverParaLinhaAbaixo(e, r, i)}
              inputProps={{
                "data-tracao-campo": r,
                "data-tracao-index": i,
              }}
              sx={{
                width: 68,
                "& .MuiInputBase-input": {
                  color: erroPercentual ? "#d32f2f" : "inherit",
                  fontWeight: erroPercentual ? 700 : 400,
                },
              }}
            />
          </TableCell>
        ))}

        <TableCell>
          <TextField
            size="small"
            value={x.cargaRuptura || ""}
            onChange={e => cp(i, "cargaRuptura", e.target.value)}
            onKeyDown={e => moverParaLinhaAbaixo(e, "cargaRuptura", i)}
            inputProps={{
              "data-tracao-campo": "cargaRuptura",
              "data-tracao-index": i,
            }}
            sx={{ width: 80 }}
          />
        </TableCell>

        <TableCell>
          <strong>{String(x.tensao || "").replace(".", ",")}</strong>
        </TableCell>
      </TableRow>
    );
  })}
</TableBody>
          </Table>
          {tipoD ? (
            <Stack spacing={1.5} mt={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Checkbox
                  checked={ensaio.exibirResumoQuantidadeTipoD !== false}
                  onChange={e => campo("exibirResumoQuantidadeTipoD", e.target.checked)}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Texto da quantidade"
                  value={ensaio.textoResumoQuantidadeTipoD || ""}
                  onChange={e => campo("textoResumoQuantidadeTipoD", e.target.value)}
                />
                <Typography sx={{ minWidth: 110 }}>
                  <strong>Quantidade:</strong> {resultadosAcima030TipoD}
                </Typography>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Checkbox
                  checked={ensaio.exibirCriterioResumoTipoD !== false}
                  onChange={e => campo("exibirCriterioResumoTipoD", e.target.checked)}
                />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  label="Texto do critério"
                  value={ensaio.textoCriterioResumoTipoD || ""}
                  onChange={e => campo("textoCriterioResumoTipoD", e.target.value)}
                />
              </Stack>
            </Stack>
          ) : (
          <Stack spacing={1.5} mt={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <Checkbox
                checked={ensaio.exibirResumoLimite1 !== false}
                onChange={e => campo("exibirResumoLimite1", e.target.checked)}
              />
              <TextField
                size="small"
                label="Limite 1 (MPa)"
                value={ensaio.limiteResumo1 || ""}
                onChange={e => campo("limiteResumo1", e.target.value)}
                sx={{ width: 150 }}
              />
              <Typography>
                <strong>Valores acima do limite:</strong> {resultadosAcimaLimite1}
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <Checkbox
                checked={ensaio.exibirResumoLimite2 !== false}
                onChange={e => campo("exibirResumoLimite2", e.target.checked)}
              />
              <TextField
                size="small"
                label="Limite 2 (MPa)"
                value={ensaio.limiteResumo2 || ""}
                onChange={e => campo("limiteResumo2", e.target.value)}
                sx={{ width: 150 }}
              />
              <Typography>
                <strong>Valores acima do limite:</strong> {resultadosAcimaLimite2}
              </Typography>
            </Stack>
          </Stack>
          )}
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">
            Quadro 2 - Ensaio de umidade do revestimento
          </Typography>

          {[0, 1, 2].map((_, i) => {
  const miTexto = String(ensaio.umidade?.[i]?.mi ?? "").trim();
  const mi = !miTexto || miTexto === "-" ? 0 : Number(miTexto.replace(",", "."));
  const mu = Number(String(ensaio.umidade?.[i]?.mu || 0).replace(",", "."));
  const ms = Number(String(ensaio.umidade?.[i]?.ms || 0).replace(",", "."));

  const w =
    ms > 0
      ? (((mu - mi) - ms) / ms) * 100
      : null;

  return (
    <Stack key={i} direction="row" spacing={1} mt={2}>
              <TextField
                label={`Nº ${i + 1} - Mi (g)`}
                value={ensaio.umidade?.[i]?.mi || ""}
                onChange={e => {
                  const u = [...(ensaio.umidade || [])];
                  u[i] = { ...(u[i] || {}), mi: e.target.value };
                  campo("umidade", u);
                }}
              />

              <TextField
                label="Mu (g)"
                value={ensaio.umidade?.[i]?.mu || ""}
                onChange={e => {
                  const u = [...(ensaio.umidade || [])];
                  u[i] = { ...(u[i] || {}), mu: e.target.value };
                  campo("umidade", u);
                }}
              />

              <TextField
                label="Ms (g)"
                value={ensaio.umidade?.[i]?.ms || ""}
                onChange={e => {
                  const u = [...(ensaio.umidade || [])];
                  u[i] = { ...(u[i] || {}), ms: e.target.value };
                  campo("umidade", u);
                }}
              />

              <TextField
  label="W (%)"
  value={
    w === null
      ? ""
      : w.toFixed(1).replace(".", ",")
  }
  InputProps={{
    readOnly: true,
  }}
  sx={{
    width: 140,
    "& .MuiInputBase-input": {
      fontWeight: 700,
    },
  }}
/>
            </Stack>
  );
})}
        </Paper>
      </Box>

      <Dialog
        open={fotosAberto}
        onClose={() => setFotosAberto(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Relatório fotográfico</DialogTitle>

        <DialogContent dividers>
          {salvando && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progressoFotos}
              />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Salvando fotos... {progressoFotos}%
              </Typography>
            </Box>
          )}

          <Box className="fotos-extracao-grid">
            {fotos.map((f, i) => (
              <Paper className="foto-extracao-card" key={f.id} sx={{ p: 2 }}>
                {(f.previewUrl || f.url) && (
                  <Box className="foto-extracao-preview">
                    <img src={f.previewUrl || f.url} alt={f.legenda} />
                  </Box>
                )}

                <Button component="label">
                  Selecionar imagem
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      setFotos(a =>
                        a.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                arquivo: file,
                                previewUrl: URL.createObjectURL(file),
                              }
                            : x
                        )
                      );
                    }}
                  />
                </Button>

                <TextField
                  fullWidth
                  size="small"
                  label="Legenda"
                  value={f.legenda}
                  onChange={e =>
                    setFotos(a =>
                      a.map((x, j) =>
                        j === i ? { ...x, legenda: e.target.value } : x
                      )
                    )
                  }
                />
              </Paper>
            ))}
          </Box>

          <Button sx={{ mt: 2 }} onClick={() => setFotos(a => [...a, fotoVazia(a.length)])}>
            + Adicionar foto
          </Button>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setFotosAberto(false)}>Cancelar</Button>

          <Button variant="contained" disabled={salvando} onClick={salvarFotos}
            sx={{ "&.botao-salvando-progresso::after": { display: "none" } }}>
            {salvando ? `Salvando ${progressoFotos}%` : "Salvar fotos"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAvisoCelulaCarga}
        onClose={() => setOpenAvisoCelulaCarga(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Atenção</DialogTitle>
        <DialogContent dividers>
          <Typography>
            É necessário escolher uma célula de carga antes de salvar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpenAvisoCelulaCarga(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmarCancelamento}
        onClose={() => setOpenConfirmarCancelamento(false)}
      >
        <DialogTitle>Cancelar alterações</DialogTitle>
        <DialogContent>
          <Typography>
            Ao confirmar você perderá todas as alterações. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmarCancelamento(false)}>
            Continuar editando
          </Button>
          <Button color="error" variant="contained" onClick={confirmarCancelarAlteracoes}>
            Descartar alterações
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
